import React from 'react';

// One button style per kind of action (ADR-49). Colours come from the theme
// tokens, so dark mode needs nothing extra.

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost';
type Size = 'md' | 'sm';

const base =
  'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors ' +
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const variants: Record<Variant, string> = {
  primary: 'bg-primary text-on-primary hover:bg-primary-hover font-semibold',
  secondary: 'bg-surface text-body border border-control-line hover:bg-canvas',
  danger: 'bg-danger text-on-danger hover:opacity-90 font-semibold',
  ghost: 'bg-transparent text-body hover:bg-canvas',
};

const sizes: Record<Size, string> = {
  md: 'min-h-[44px] px-4 text-sm',
  sm: 'min-h-[36px] px-3 text-[13px]',
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'secondary', size = 'md', className = '', type = 'button', ...rest }, ref) => (
    <button
      ref={ref}
      type={type}
      className={`${base} ${variants[variant]} ${sizes[size]} ${className}`.trim()}
      {...rest}
    />
  ),
);
Button.displayName = 'Button';

export const buttonClasses = (variant: Variant = 'secondary', size: Size = 'md') =>
  `${base} ${variants[variant]} ${sizes[size]}`;
